import { definePlatformSetting } from '@crest/platform-contracts/settings';
import { z } from 'zod';
export const documentsSignedPoliciesDisplayValueSchema = z.strictObject({
    density: z.enum(['comfortable', 'compact']),
    cardSize: z.enum(['standard', 'wide']),
    showVersionMetadata: z.boolean(),
    previewScale: z.union([z.literal(75), z.literal(90), z.literal(100)]),
});
export const documentsSignedPoliciesDisplaySetting = definePlatformSetting({
    definition: {
        key: 'documents.signed-policies.display', moduleId: 'documents',
        title: 'Signed policy display',
        helpText: 'Controls the signed-policy list, signing workspace, and retained printable preview.',
        configurationDomain: 'layout', valueType: 'object', schemaVersion: 1,
        codedFallback: { density: 'comfortable', cardSize: 'wide', showVersionMetadata: true, previewScale: 90 },
        permittedScopes: ['platform', 'plan', 'tenant', 'user'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: true, offlineEligible: false, safetyClass: 'ordinary',
    }, valueSchema: documentsSignedPoliciesDisplayValueSchema,
});
export const documentsSignedPoliciesCopyValueSchema = z.strictObject({
    navigationLabel: z.string().trim().min(1).max(60),
    title: z.string().trim().min(1).max(120),
    description: z.string().trim().min(1).max(400),
    availableTitle: z.string().trim().min(1).max(120),
    historyTitle: z.string().trim().min(1).max(120),
    policyLabel: z.string().trim().min(1).max(80),
    versionLabel: z.string().trim().min(1).max(60),
    publishedVersionLabel: z.string().trim().min(1).max(100),
    pageLabel: z.string().trim().min(1).max(60),
    previewLabel: z.string().trim().min(1).max(100),
    initialsLabel: z.string().trim().min(1).max(80),
    pageInitialsLabel: z.string().trim().min(1).max(120),
    typedNameLabel: z.string().trim().min(1).max(80),
    signatureFileLabel: z.string().trim().min(1).max(100),
    acknowledgementLabel: z.string().trim().min(1).max(100),
    acknowledgementFallback: z.string().trim().min(1).max(500),
    consentLabel: z.string().trim().min(1).max(160),
    reasonLabel: z.string().trim().min(1).max(80),
    signingReasonFallback: z.string().trim().min(5).max(500),
    finalizeReasonFallback: z.string().trim().min(5).max(500),
    signAction: z.string().trim().min(1).max(80),
    finalPdfLabel: z.string().trim().min(1).max(100),
    finalizeAction: z.string().trim().min(1).max(80),
    loadingLabel: z.string().trim().min(1).max(120),
    savingLabel: z.string().trim().min(1).max(120),
    loadError: z.string().trim().min(1).max(240),
    mutationError: z.string().trim().min(1).max(240),
    sourceUnavailable: z.string().trim().min(1).max(240),
    emptyAvailable: z.string().trim().min(1).max(240),
    emptyHistory: z.string().trim().min(1).max(240),
    savedLabel: z.string().trim().min(1).max(160),
    statusLabels: z.strictObject({ signed: z.string().trim().min(1).max(60), finalized: z.string().trim().min(1).max(60) }),
});
export const documentsSignedPoliciesCopySetting = definePlatformSetting({
    definition: {
        key: 'documents.copy.signed-policies', moduleId: 'documents',
        title: 'Signed policy copy', helpText: 'Controls tenant-editable signing and readback labels.',
        configurationDomain: 'copy', valueType: 'object', schemaVersion: 1,
        codedFallback: {
            navigationLabel: 'Signed policies', title: 'Sign and review policies',
            description: 'Review the exact published Knowledge version, initial every page, sign, and retain proof.',
            availableTitle: 'Policies ready for signature', historyTitle: 'My signed policy records',
            policyLabel: 'Published policy', versionLabel: 'Version',
            publishedVersionLabel: 'Published version', pageLabel: 'Page',
            previewLabel: 'Signed policy preview', initialsLabel: 'Signer initials',
            pageInitialsLabel: 'Page initials', typedNameLabel: 'Typed legal name',
            signatureFileLabel: 'Drawn signature image', acknowledgementLabel: 'Acknowledgement statement',
            acknowledgementFallback: 'I acknowledge this exact published policy version.',
            consentLabel: 'I consent to use this electronic signature for the exact policy version shown.',
            reasonLabel: 'Signing reason', signingReasonFallback: 'Sign exact published Knowledge policy version',
            finalizeReasonFallback: 'Attach the reviewed final signed policy PDF',
            signAction: 'Sign exact policy version',
            finalPdfLabel: 'Reviewed final signed PDF', finalizeAction: 'Attach final PDF',
            loadingLabel: 'Loading signed policies…', savingLabel: 'Verifying and saving signature evidence…',
            loadError: 'Signed policies could not be loaded.',
            mutationError: 'The signed-policy action could not be completed. Refresh and try again.',
            sourceUnavailable: 'Knowledge policies are unavailable or you do not have permission to view them.',
            emptyAvailable: 'No published Knowledge policies require a controlled signature.',
            emptyHistory: 'No signed policy records are available.',
            savedLabel: 'The signed-policy action completed successfully.',
            statusLabels: { signed: 'Signed', finalized: 'Final PDF attached' },
        },
        permittedScopes: ['platform', 'plan', 'tenant'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: false, offlineEligible: false, safetyClass: 'ordinary',
    }, valueSchema: documentsSignedPoliciesCopyValueSchema,
});
//# sourceMappingURL=documents-signed-policy-setting-definitions.js.map